/**
 * Painel de conexão da conta de anúncios da Meta.
 *
 * Usado em Configurações → Integrações → Meta Ads. Uma credencial ativa por
 * workspace: o token é de Usuário do Sistema, colado pelo admin, e alcança
 * todas as contas de anúncio do Business Manager.
 *
 * O token nunca volta do servidor — quando já existe credencial salva, o campo
 * mostra só a máscara (`••••••••1234`) e fica vazio para digitação.
 */

import { useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Megaphone,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Trash2,
  RefreshCw,
  Star,
  Eye,
  EyeOff,
  Facebook,
  BookOpen,
} from "lucide-react";
import { toast } from "sonner";

/**
 * `account_status` da Meta. 1 e 9 aceitam alteração; o resto não, e o motivo
 * precisa aparecer aqui — senão o admin só descobre quando uma criação falha
 * com "code 368", que não diz nem qual é o problema nem onde se resolve.
 */
const ACCOUNT_STATUS: Record<number, { label: string; tone: "ok" | "warn" | "bad" }> = {
  1: { label: "Ativa", tone: "ok" },
  2: { label: "Desativada", tone: "bad" },
  3: { label: "Fatura em aberto", tone: "bad" },
  7: { label: "Em análise de risco", tone: "warn" },
  9: { label: "Período de carência", tone: "warn" },
  101: { label: "Encerrada", tone: "bad" },
};

function AccountStatusBadge({ status }: { status: number | null }) {
  if (status == null) return null;
  const info = ACCOUNT_STATUS[status] ?? { label: `Status ${status}`, tone: "warn" as const };
  const cls =
    info.tone === "ok"
      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"
      : info.tone === "warn"
        ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
        : "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300";
  return <Badge className={cls}>{info.label}</Badge>;
}

/** Converte centavos digitados no campo de teto para número, ou null. */
function parseBudgetCap(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed.replace(/\D/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function MetaAdsPanel() {
  const utils = trpc.useUtils();
  const { can } = usePermissions();
  const [, navigate] = useLocation();
  // Mesmo cuidado da tela /ads: quem tem settings:view mas não
  // ads:credentials_manage abriria este painel e dispararia um FORBIDDEN que o
  // handler global engole, deixando um formulário vazio sem explicação.
  const podeGerenciar = can("ads:credentials_manage");
  const [token, setToken] = useState("");
  const [budgetCap, setBudgetCap] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const credential = trpc.metaAds.credential.get.useQuery(undefined, {
    enabled: podeGerenciar,
  });

  /**
   * Valor efetivo do campo de teto.
   *
   * `null` significa "o usuário não digitou nada", e nesse caso vale o que já
   * está salvo. Antes o campo nascia como string vazia e só mostrava o valor
   * salvo no placeholder: quem trocasse o token sem tocar no campo enviava
   * `budgetCapCents: null` e apagava silenciosamente o teto de gasto — a
   * proteção que o módulo inteiro anuncia como freio de mão sumia sem aviso.
   */
  const capAtual = credential.data?.budgetCapCents ?? null;
  const capNoCampo = budgetCap ?? (capAtual != null ? String(capAtual) : "");
  // `includeAll`: aqui o admin decide o que fica visível, então precisa ver
  // também as ocultas e as inativas — que as outras telas não mostram.
  const accounts = trpc.metaAds.accounts.list.useQuery({ includeAll: true }, {
    enabled: !!credential.data,
  });

  const invalidate = () => {
    void utils.metaAds.credential.get.invalidate();
    void utils.metaAds.accounts.list.invalidate();
  };

  const oauth = trpc.metaAds.credential.oauthState.useQuery(undefined, {
    enabled: podeGerenciar,
  });

  const conectarOAuth = trpc.metaAds.credential.connectFromOAuth.useMutation({
    onSuccess: () => {
      invalidate();
      void utils.metaAds.credential.oauthState.invalidate();
      toast.success("Conta de anúncios conectada pelo Facebook.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const save = trpc.metaAds.credential.save.useMutation({
    onSuccess: () => {
      setToken("");
      // Volta a espelhar o que foi salvo, em vez de manter o texto digitado.
      setBudgetCap(null);
      invalidate();
      toast.success("Conta da Meta conectada.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const test = trpc.metaAds.credential.test.useMutation({
    onSuccess: (res) => {
      invalidate();
      toast.success(
        `Conexão ok — ${res.accounts.length} conta(s) de anúncio encontrada(s).`,
      );
    },
    onError: (err) => {
      invalidate();
      toast.error(toUserFriendlyError(err));
    },
  });

  const remove = trpc.metaAds.credential.remove.useMutation({
    onSuccess: () => {
      invalidate();
      setConfirmRemove(false);
      toast.success("Conta da Meta desconectada.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const setHidden = trpc.metaAds.accounts.setHidden.useMutation({
    onSuccess: (_r, v) => {
      void utils.metaAds.accounts.list.invalidate();
      toast.success(v.hidden ? "Conta ocultada das listas." : "Conta visível de novo.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const [manter, setManter] = useState<Set<string>>(new Set());
  const ocultarTodas = trpc.metaAds.accounts.hideAllExcept.useMutation({
    onSuccess: (r) => {
      void utils.metaAds.accounts.list.invalidate();
      setManter(new Set());
      toast.success(`${r.ocultadas} conta(s) ocultada(s). Só a padrão e as marcadas continuam nas listas.`);
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const setDefault = trpc.metaAds.accounts.setDefault.useMutation({
    onSuccess: () => {
      void utils.metaAds.accounts.list.invalidate();
      toast.success("Conta padrão atualizada.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const updateCap = trpc.metaAds.credential.update.useMutation({
    onSuccess: () => {
      void utils.metaAds.credential.get.invalidate();
      toast.success("Teto de orçamento atualizado.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const cred = credential.data;
  const busy = save.isPending || test.isPending || remove.isPending;

  if (!podeGerenciar) {
    return (
      <p className="text-sm text-muted-foreground">
        Você não tem permissão para gerenciar a conexão com a Meta. Peça a um administrador do
        workspace.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-blue-100 p-2 dark:bg-blue-900/40">
          <Megaphone className="h-5 w-5 text-blue-700 dark:text-blue-300" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">Meta Ads</h3>
          <p className="text-sm text-muted-foreground">
            Conecte a conta de anúncios para acompanhar e gerenciar campanhas de tráfego pelo
            LeadConversa.
          </p>
        </div>
        {cred && (
          <Badge className="ml-auto bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
            Conectado
          </Badge>
        )}
      </div>

      {/* ── Estado da conexão ─────────────────────────────────────────────── */}
      {cred && (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium">Token</span>
              <code className="rounded bg-muted px-2 py-1 text-sm">{cred.maskedToken}</code>
              <span className="text-xs text-muted-foreground">API {cred.apiVersion}</span>
            </div>

            {/* Token de Usuário do Sistema não expira, então nada avisa quando
                alguém o revoga no Business Manager. Esta linha é o único sinal
                que o admin tem antes de a falha aparecer numa operação. */}
            <div className="flex items-center gap-2 text-sm">
              {cred.lastVerifyStatus === "ok" ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              ) : cred.lastVerifyStatus === "failed" ? (
                <AlertCircle className="h-4 w-4 text-red-600" />
              ) : null}
              <span className="text-muted-foreground">
                {cred.lastVerifiedAt
                  ? `Última verificação: ${new Date(cred.lastVerifiedAt).toLocaleString("pt-BR")}`
                  : "Ainda não verificado"}
              </span>
            </div>
            {cred.lastVerifyStatus === "failed" && cred.lastVerifyError && (
              <p className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
                {cred.lastVerifyError}
              </p>
            )}

            <div className="flex gap-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => test.mutate()}
                disabled={busy}
              >
                {test.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Testar conexão
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 hover:text-red-700"
                onClick={() => setConfirmRemove(true)}
                disabled={busy}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Desconectar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Token ─────────────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="space-y-4 pt-6">
          {oauth.data?.pendente && (
            <div className="space-y-2 rounded border border-blue-300 bg-blue-50 p-3 dark:border-blue-900 dark:bg-blue-950/40">
              <p className="text-sm font-medium">Autorização do Facebook recebida</p>
              <p className="text-xs text-muted-foreground">
                Falta confirmar para o LeadConversa começar a enxergar suas contas de anúncio.
              </p>
              <Button
                onClick={() => conectarOAuth.mutate({ apiVersion: undefined, budgetCapCents: parseBudgetCap(capNoCampo) })}
                disabled={conectarOAuth.isPending}
              >
                {conectarOAuth.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Concluir conexão
              </Button>
            </div>
          )}

          {oauth.data?.disponivel && !oauth.data?.pendente && (
            <div className="space-y-2">
              <Button
                className="w-full bg-[#1877F2] text-white hover:bg-[#166FE5]"
                onClick={() => {
                  // Página inteira, não fetch: OAuth é redirect para o domínio
                  // da Meta e volta.
                  window.location.href = "/api/integrations/meta/connect?purpose=ads";
                }}
              >
                <Facebook className="mr-2 h-4 w-4" />
                {cred ? "Reconectar com o Facebook" : "Entrar com Facebook"}
              </Button>
              <p className="text-xs text-muted-foreground">
                Autoriza o acesso às suas contas de anúncio sem você precisar gerar token à mão.
              </p>
            </div>
          )}

          {oauth.data?.disponivel && (
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs text-muted-foreground">ou cole um token</span>
              <div className="h-px flex-1 bg-border" />
            </div>
          )}

          {/* O manual é aberto DAQUI porque é aqui que a pessoa trava: a
              parte difícil da conexão acontece no app do Instagram e no
              Gerenciador de Negócios, fora desta tela. */}
          <button
            type="button"
            onClick={() => navigate("/conectar-meta")}
            className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
          >
            <BookOpen className="h-3.5 w-3.5" />
            Ver o passo a passo completo
          </button>

          <div className="space-y-2">
            <Label htmlFor="meta-ads-token">
              {cred ? "Substituir token de acesso" : "Token de acesso (Usuário do Sistema)"}
            </Label>
            <Input
              id="meta-ads-token"
              type="password"
              autoComplete="off"
              placeholder={cred ? "Cole um novo token para substituir" : "EAAG..."}
              value={token}
              onChange={(e) => setToken(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              No Gerenciador de Negócios da Meta, crie um Usuário do Sistema com acesso à conta
              de anúncios e gere um token com as permissões <code>ads_management</code>,{" "}
              <code>ads_read</code> e <code>business_management</code>. O token é verificado
              antes de ser salvo e fica criptografado no banco.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="meta-ads-cap">Teto de orçamento diário (centavos, opcional)</Label>
            <Input
              id="meta-ads-cap"
              inputMode="numeric"
              placeholder="Ex: 20000 = R$ 200,00"
              value={capNoCampo}
              onChange={(e) => setBudgetCap(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Nenhuma campanha criada pelo LeadConversa poderá passar deste valor por dia. Deixe
              vazio para não limitar.
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() =>
                save.mutate({
                  accessToken: token.trim(),
                  budgetCapCents: parseBudgetCap(capNoCampo),
                })
              }
              disabled={busy || token.trim().length < 20}
            >
              {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {cred ? "Substituir e verificar" : "Conectar e verificar"}
            </Button>
            {cred && (
              <Button
                variant="outline"
                onClick={() =>
                  updateCap.mutate({ id: cred.id, budgetCapCents: parseBudgetCap(capNoCampo) })
                }
                disabled={updateCap.isPending}
              >
                {updateCap.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Salvar só o teto
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Contas de anúncio ─────────────────────────────────────────────── */}
      {cred && (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="font-medium">Contas de anúncio</h4>
              <span className="text-xs text-muted-foreground">
                Nas outras telas só aparecem as contas visíveis e ativas. Marque as que quer manter e oculte o resto.
              </span>
            </div>
            {(accounts.data?.filter((a) => !a.isHidden && !a.isDefault).length ?? 0) > 1 && (
              <div className="flex flex-wrap items-center gap-2 rounded border border-dashed p-2 text-xs">
                <span className="text-muted-foreground">Muitas contas que não usa?</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => ocultarTodas.mutate({ manter: [...manter] })}
                  disabled={ocultarTodas.isPending}
                >
                  {ocultarTodas.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <EyeOff className="mr-1 h-3 w-3" />}
                  Ocultar todas menos a padrão{manter.size > 0 ? ` e ${manter.size} marcada(s)` : ""}
                </Button>
              </div>
            )}

            {accounts.isLoading && (
              <p className="text-sm text-muted-foreground">Carregando contas…</p>
            )}
            {accounts.data?.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Nenhuma conta encontrada. Use “Testar conexão” para sincronizar.
              </p>
            )}

            <div className="space-y-2">
              {accounts.data?.map((acc) => (
                <div
                  key={acc.accountId}
                  className={`flex flex-wrap items-center gap-3 rounded border p-3 ${acc.isHidden ? "opacity-60" : ""}`}
                >
                  {!acc.isDefault && !acc.isHidden && (
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={manter.has(acc.accountId)}
                      onChange={(e) =>
                        setManter((s) => {
                          const n = new Set(s);
                          if (e.target.checked) n.add(acc.accountId);
                          else n.delete(acc.accountId);
                          return n;
                        })
                      }
                      title="Manter visível ao ocultar as demais"
                      aria-label="Manter esta conta"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{acc.name ?? acc.accountId}</span>
                      {acc.isDefault && (
                        <Badge variant="secondary" className="gap-1">
                          <Star className="h-3 w-3" /> Padrão
                        </Badge>
                      )}
                      <AccountStatusBadge status={acc.accountStatus} />
                      {acc.isHidden && <Badge variant="outline">Oculta</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {acc.accountId}
                      {acc.currency ? ` · ${acc.currency}` : ""}
                      {acc.timezoneName ? ` · ${acc.timezoneName}` : ""}
                    </p>
                  </div>
                  {!acc.isDefault && !acc.isHidden && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDefault.mutate({ accountId: acc.accountId })}
                      disabled={setDefault.isPending}
                    >
                      Tornar padrão
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setHidden.mutate({ accountId: acc.accountId, hidden: !acc.isHidden })}
                    disabled={setHidden.isPending}
                    title={acc.isHidden ? "Voltar a mostrar nas listas" : "Ocultar das listas de contas"}
                  >
                    {acc.isHidden ? <Eye className="mr-1 h-4 w-4" /> : <EyeOff className="mr-1 h-4 w-4" />}
                    {acc.isHidden ? "Mostrar" : "Ocultar"}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desconectar a conta da Meta?</AlertDialogTitle>
            <AlertDialogDescription>
              O token salvo é apagado e a lista de contas de anúncio some do LeadConversa. As
              campanhas que já existem na Meta continuam rodando normalmente — nada é pausado
              nem excluído lá.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => cred && remove.mutate({ id: cred.id })}
            >
              Desconectar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
